import type { JSX } from 'react';
import { ChevronRight, FileText, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ManifestDoc } from './docsManifest';

interface HelpPopoverProps {
  /** The current screen's doc; null when it has none (yet). */
  doc: ManifestDoc | null;
  /** The screen's own title, shown when it has no doc. */
  screenTitle?: string;
  isLoading: boolean;
  isError: boolean;
  /**
   * False once the user can see no doc at all: the full docs would open on a
   * doc behind a flag that is off, so the help leaves the way in out.
   */
  canOpenFullDocs: boolean;
  onOpenFullDocs: () => void;
  onClose: () => void;
}

/** Per-screen help (Mockup 1): a short summary, "On this screen", and the way into the full docs. */
export function HelpPopover({
  doc,
  screenTitle,
  isLoading,
  isError,
  canOpenFullDocs,
  onOpenFullDocs,
  onClose,
}: HelpPopoverProps): JSX.Element {
  const title = doc?.title ?? screenTitle ?? 'This screen';
  return (
    <div className="flex flex-col">
      <div className="flex items-start gap-3 border-b px-4 py-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300">
          <FileText aria-hidden="true" className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-foreground">Help</p>
          <p className="truncate text-sm font-semibold">{title}</p>
        </div>
        <button
          type="button"
          aria-label="Close help"
          onClick={onClose}
          className="-mr-1 cursor-pointer rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>
      <div className="flex flex-col gap-3 px-4 py-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading help…</p>
        ) : isError ? (
          <p className="text-sm text-muted-foreground">
            Help for this screen could not be loaded. Try again later.
          </p>
        ) : doc ? (
          <>
            {doc.helpSummary && (
              <p className="text-sm leading-relaxed">{doc.helpSummary}</p>
            )}
            {doc.helpItems.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <p className="text-sm font-semibold">On this screen</p>
                <ul className="flex list-disc flex-col gap-1 pl-5 text-sm">
                  {doc.helpItems.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            There is no help for this screen yet.
          </p>
        )}
        {canOpenFullDocs && (
          <Button
            size="sm"
            className="mt-1 self-start bg-violet-600 text-white hover:bg-violet-700 dark:bg-violet-500 dark:text-white dark:hover:bg-violet-400"
            disabled={isLoading}
            onClick={onOpenFullDocs}
          >
            Open full documentation
            <ChevronRight aria-hidden="true" />
          </Button>
        )}
      </div>
    </div>
  );
}
